/**
 * Parses a TL;DR-style summary from the top of a blog post markdown body.
 *
 * What: Detects an H2 section whose heading matches common summary labels ("TL;DR", "Summary",
 *       "In brief", "Key takeaways", etc.), captures its items (bullets or paragraphs), and
 *       returns both the structured summary and the markdown with that section removed.
 * Why:  Separates summary extraction from rendering so the BlogTldrCallout component can style
 *       the summary independently of the main article body, and so the section appears exactly
 *       once in the rendered page.
 * When: Update when a new summary label should be recognised, or when the article body shape
 *       changes (e.g. moving from H2 to a different heading level).
 */

export interface BlogTldrContent {
  /** `'list'` renders as a bulleted list; `'paragraph'` renders as stacked paragraphs. */
  kind: 'list' | 'paragraph';
  /** Raw inline markdown per bullet (for `list`) or per paragraph (for `paragraph`). */
  items: string[];
  /** The heading text as authored, preserved for display (e.g. `"TL;DR"`, `"Key Takeaways"`). */
  label: string;
}

export interface ExtractTldrResult {
  tldr: BlogTldrContent | null;
  /** Markdown with the TL;DR section removed (heading + body up to the next H2). */
  strippedMarkdown: string;
}

// Accepted heading texts — matched case-insensitively, with optional colons or punctuation
// trimmed. Anchored to the start/end of the heading so a heading like "Why TL;DR Matters"
// does NOT get interpreted as a summary section.
const TLDR_LABEL_PATTERNS: readonly RegExp[] = [
  /^tl[;:]?\s*dr$/i,
  /^tl\s*\/\s*dr$/i,
  /^tldr$/i,
  /^summary$/i,
  /^in\s+brief$/i,
  /^in\s+short$/i,
  /^key\s+takeaways?$/i,
  /^quick\s+summary$/i,
  /^bottom\s+line$/i,
  /^the\s+short\s+version$/i,
];

function isTldrHeadingLabel(headingText: string): boolean {
  const normalized = headingText
    .trim()
    .replace(/^[*_`]+|[*_`]+$/g, '') // strip wrapping bold/italic markers
    .replace(/[.:!?]+$/, '')
    .trim();
  if (!normalized) {
    return false;
  }
  return TLDR_LABEL_PATTERNS.some(pattern => pattern.test(normalized));
}

function isBulletLine(line: string): boolean {
  return /^\s*(?:[-*+]|\d+\.)\s+/.test(line);
}

function stripBulletMarker(line: string): string {
  return line.replace(/^\s*(?:[-*+]|\d+\.)\s+/, '').trim();
}

/**
 * Splits an array of raw captured lines into either a list (when any line is a bullet)
 * or stacked paragraphs (when no bullets are present). Continuation lines of a bullet
 * (indented or following a bullet without a blank line) are joined into the same item.
 */
function buildTldrContent(
  lines: readonly string[],
  label: string
): BlogTldrContent | null {
  const hasBullets = lines.some(isBulletLine);

  if (hasBullets) {
    const items: string[] = [];
    let current: string[] = [];

    const commit = () => {
      const joined = current.join(' ').replace(/\s+/g, ' ').trim();
      if (joined) {
        items.push(joined);
      }
      current = [];
    };

    for (const rawLine of lines) {
      if (isBulletLine(rawLine)) {
        commit();
        current.push(stripBulletMarker(rawLine));
        continue;
      }
      const trimmed = rawLine.trim();
      if (!trimmed) {
        // Blank line: keep current bullet open; continuations still attach.
        continue;
      }
      if (current.length === 0) {
        // Pre-list paragraph text — skip, list content is the authoritative summary shape.
        continue;
      }
      current.push(trimmed);
    }
    commit();

    if (items.length === 0) {
      return null;
    }
    return { kind: 'list', items, label };
  }

  // Paragraph mode — split on blank lines.
  const paragraphs: string[] = [];
  let current: string[] = [];
  const commit = () => {
    const joined = current.join(' ').replace(/\s+/g, ' ').trim();
    if (joined) {
      paragraphs.push(joined);
    }
    current = [];
  };

  for (const rawLine of lines) {
    const trimmed = rawLine.trim();
    if (!trimmed) {
      commit();
      continue;
    }
    current.push(trimmed);
  }
  commit();

  if (paragraphs.length === 0) {
    return null;
  }
  return { kind: 'paragraph', items: paragraphs, label };
}

/**
 * Extracts the first TL;DR-style H2 section from the given markdown body.
 *
 * The extraction is conservative:
 *  - Only H2 (`##`) headings are considered — H3s are never treated as summaries.
 *  - Only the first matching section is extracted. Subsequent summary-like headings (rare) are
 *    left in the body to avoid removing content the author expects to render inline.
 *  - If extraction fails for any reason, the original markdown is returned unchanged.
 */
export function extractTldrFromMarkdown(markdown: string): ExtractTldrResult {
  if (typeof markdown !== 'string' || !markdown.trim()) {
    return { tldr: null, strippedMarkdown: markdown ?? '' };
  }

  const lines = markdown.split(/\r?\n/);
  let startIndex = -1;
  let label = '';
  let endIndex = lines.length;

  for (let i = 0; i < lines.length; i += 1) {
    const match = /^##\s+(.+?)\s*$/.exec(lines[i]);
    if (!match) {
      continue;
    }
    if (startIndex === -1 && isTldrHeadingLabel(match[1])) {
      startIndex = i;
      label = match[1]
        .trim()
        .replace(/[.:!?]+$/, '')
        .trim();
      continue;
    }
    if (startIndex !== -1) {
      endIndex = i;
      break;
    }
  }

  if (startIndex === -1) {
    return { tldr: null, strippedMarkdown: markdown };
  }

  const sectionLines = lines.slice(startIndex + 1, endIndex);
  const tldr = buildTldrContent(sectionLines, label);
  if (!tldr) {
    return { tldr: null, strippedMarkdown: markdown };
  }

  // Drop the TL;DR heading and its body. Also collapse a leading run of blank lines so the
  // remaining article body doesn't start with awkward whitespace.
  const remainingLines = [
    ...lines.slice(0, startIndex),
    ...lines.slice(endIndex),
  ];
  let leading = 0;
  while (
    leading < remainingLines.length &&
    remainingLines[leading].trim() === ''
  ) {
    leading += 1;
  }
  const strippedMarkdown = remainingLines.slice(leading).join('\n');

  return { tldr, strippedMarkdown };
}
